/**
 * /dna-verified — public landing page explaining the DNA-Verified
 * standard for buyers. Linked from the gold badge tooltip + the
 * footer Trust section.
 */
import React from "react";
import { Link } from "react-router-dom";
import PublicShell from "@/components/PublicShell";
import { TestTubeIcon as TestTube, CheckCircleIcon as CheckCircle, XIcon as X, WarningIcon as Warning, ShieldCheckIcon as ShieldCheck, CertificateIcon as Certificate } from "@phosphor-icons/react";

export default function WhyDNAVerified() {
  return (
    <PublicShell>
      {/* Hero */}
      <section className="bg-[#0E0E0E] text-white rounded-2xl p-8 md:p-12 mb-10 relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#C9A227]/20 blur-3xl"
        />
        <div className="relative max-w-3xl">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
            Buyer trust standard
          </p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Why <span className="text-gold-light">DNA-Verified</span> matters
            when you buy a puppy.
          </h1>
          <p className="text-white/70 mt-4 max-w-2xl leading-relaxed">
            The gold DNA-Verified badge means parentage on this dog was
            confirmed by an Armstrong K9 Registry administrator against a
            third-party DNA panel report from a recognized lab. It's the
            one badge on this platform that the breeder cannot fake — the
            chain of custody runs through us.
          </p>
        </div>
      </section>

      {/* What the badge means */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <div className="bg-white border border-[#EAEAEA] rounded-xl p-6">
          <h2 className="font-spectral text-2xl mb-3 flex items-center gap-2">
            <CheckCircle size={22} weight="duotone" className="text-green-700" />
            What it means
          </h2>
          <ul className="space-y-2 text-sm text-[#3a3a3a] leading-relaxed">
            <li className="flex gap-2"><CheckCircle size={14} weight="fill" className="text-green-700 mt-0.5 flex-shrink-0" /> The breeder uploaded a DNA panel report from a recognized lab (Embark, Wisdom Panel, VGL, or equivalent).</li>
            <li className="flex gap-2"><CheckCircle size={14} weight="fill" className="text-green-700 mt-0.5 flex-shrink-0" /> An Armstrong K9 Registry administrator reviewed the panel and confirmed the parentage claim matches the registered sire and dam.</li>
            <li className="flex gap-2"><CheckCircle size={14} weight="fill" className="text-green-700 mt-0.5 flex-shrink-0" /> The breeder paid a $75 verification fee — they have skin in the game.</li>
            <li className="flex gap-2"><CheckCircle size={14} weight="fill" className="text-green-700 mt-0.5 flex-shrink-0" /> The badge is automatically revoked if the breeder requests a refund or transfers the dog out without re-affirmation.</li>
          </ul>
        </div>

        <div className="bg-[#FEF7F0] border border-[#F5DDC8] rounded-xl p-6">
          <h2 className="font-spectral text-2xl mb-3 flex items-center gap-2">
            <X size={22} weight="duotone" className="text-crimson" />
            What it does NOT mean
          </h2>
          <ul className="space-y-2 text-sm text-[#3a3a3a] leading-relaxed">
            <li className="flex gap-2"><Warning size={14} weight="fill" className="text-crimson mt-0.5 flex-shrink-0" /> Armstrong K9 Registry did <strong>not</strong> independently lab-test the dog. We are not a genetics laboratory.</li>
            <li className="flex gap-2"><Warning size={14} weight="fill" className="text-crimson mt-0.5 flex-shrink-0" /> We do not certify or guarantee the accuracy of the underlying lab's methodology or results.</li>
            <li className="flex gap-2"><Warning size={14} weight="fill" className="text-crimson mt-0.5 flex-shrink-0" /> A DNA-Verified pedigree does <strong>not</strong> imply health clearances (OFA, PennHIP, eye CERF, etc.). Those are separate.</li>
            <li className="flex gap-2"><Warning size={14} weight="fill" className="text-crimson mt-0.5 flex-shrink-0" /> The badge is not a substitute for asking the breeder for the raw panel PDF, a health guarantee, or a sales contract.</li>
          </ul>
        </div>
      </section>

      {/* The verification process */}
      <section className="mb-10">
        <h2 className="font-spectral text-3xl tracking-tight mb-4 flex items-center gap-3">
          <TestTube size={28} weight="duotone" className="text-gold" />
          How the verification works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { n: 1, title: "Breeder pays $75", body: "The kennel purchases a DNA-Verified Pedigree Registration on a specific dog. The fee is non-refundable once review starts." },
            { n: 2, title: "Upload panel report", body: "The breeder uploads the PDF or image of their lab's parentage panel (Embark / Wisdom Panel / VGL / equivalent)." },
            { n: 3, title: "Admin review", body: "An Armstrong K9 Registry administrator manually compares the panel parents to the registered sire and dam. Decision within 2 business days." },
            { n: 4, title: "Badge goes live", body: "On approval, the gold DNA-Verified badge appears on the dog's profile, every marketplace listing, and the kennel's public page." },
          ].map((step) => (
            <div key={step.n} className="bg-white border border-[#EAEAEA] rounded-xl p-5">
              <div className="text-gold font-spectral text-3xl mb-2">{String(step.n).padStart(2, "0")}</div>
              <h3 className="font-spectral text-lg mb-1">{step.title}</h3>
              <p className="text-xs text-[#6B6B6B] leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* For buyers: how to evaluate */}
      <section className="bg-white border border-[#EAEAEA] rounded-xl p-6 md:p-8 mb-10">
        <h2 className="font-spectral text-3xl tracking-tight mb-4 flex items-center gap-3">
          <ShieldCheck size={28} weight="duotone" className="text-ink" />
          Buyer checklist — even when DNA-Verified
        </h2>
        <p className="text-sm text-[#6B6B6B] max-w-3xl leading-relaxed mb-4">
          DNA-Verified is one signal in a stack. Treat it like the receipt
          on a watch — it proves provenance, not condition. Pair it with:
        </p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <li className="flex gap-2"><Certificate size={16} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" /> Ask the breeder to email you the raw DNA panel PDF directly from the lab.</li>
          <li className="flex gap-2"><Certificate size={16} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" /> Ask for OFA / PennHIP / eye CERF documentation on sire AND dam.</li>
          <li className="flex gap-2"><Certificate size={16} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" /> Get a written health guarantee covering at least 2 years.</li>
          <li className="flex gap-2"><Certificate size={16} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" /> Use escrow for the payment — Armstrong's built-in escrow protects you.</li>
          <li className="flex gap-2"><Certificate size={16} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" /> Visit the kennel or do a video tour before paying anything.</li>
          <li className="flex gap-2"><Certificate size={16} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" /> Verify any award case links — every entry on a kennel's award case should be checkable.</li>
        </ul>
      </section>

      {/* For breeders: how to add the badge */}
      <section className="bg-gradient-to-br from-[#FBF7EC] to-white border border-gold/40 rounded-xl p-6 md:p-8 mb-10">
        <h2 className="font-spectral text-2xl tracking-tight mb-2">Are you a breeder?</h2>
        <p className="text-sm text-[#3a3a3a] leading-relaxed mb-4 max-w-2xl">
          Add the gold DNA-Verified badge to any of your registered dogs.
          Open the dog's profile and tap{" "}
          <span className="font-semibold text-ink">Submit for DNA Verification</span>{" "}
          — $75 per dog, reviewed within two business days.
        </p>
        <Link
          to="/dogs"
          className="inline-flex items-center gap-2 rounded-full bg-ink hover:bg-crimson text-white font-bold uppercase tracking-wider text-xs px-5 py-2"
          data-testid="why-dna-cta-breeder"
        >
          Go to my dogs →
        </Link>
      </section>

      {/* Legal footnote */}
      <section className="text-xs text-[#6B6B6B] leading-relaxed max-w-3xl mb-8">
        <strong className="text-ink">Important.</strong> Armstrong K9 Registry
        is not a genetics laboratory. The DNA-Verified badge reflects an
        administrative review of a third-party DNA panel report submitted by
        the breeder. We do not lab-test the dog ourselves and we make no
        representation as to the accuracy of the underlying lab's
        methodology or results. The badge is removed automatically if the
        verification fee is refunded. See our{" "}
        <Link to="/privacy" className="underline">Privacy Policy</Link> §2.11
        and <Link to="/terms" className="underline">Terms of Service</Link> §7a
        for details.
      </section>
    </PublicShell>
  );
}
